import { workspaceName } from "@fleetminder/core";

export const configNamespace = `${workspaceName} Config`;

type EnvSource = Record<string, string | undefined>;

type FieldKind = "integer" | "postgres-url" | "string" | "url";

type BaseField = {
  readonly description: string;
  readonly required?: boolean;
};

type EnumField = BaseField & {
  readonly choices: readonly [string, ...string[]];
  readonly kind: "enum";
};

type ScalarField = BaseField & {
  readonly kind: FieldKind;
  readonly minValue?: number;
  readonly minLength?: number;
};

type EnvField = EnumField | ScalarField;
type EnvSchema = Record<string, EnvField>;

type RequiredValue<TField extends EnvField> = TField["required"] extends false
  ? string | undefined
  : string;

export type ValidatedEnv<TSchema extends EnvSchema> = {
  readonly [TKey in keyof TSchema]: RequiredValue<TSchema[TKey]>;
};

export type EnvIssue = {
  readonly message: string;
  readonly name: string;
};

export class EnvValidationError extends Error {
  readonly issues: readonly EnvIssue[];

  constructor(scope: string, issues: readonly EnvIssue[]) {
    super(formatEnvIssues(scope, issues));
    this.name = "EnvValidationError";
    this.issues = issues;
  }
}

export const webEnvSchema = {
  VITE_API_BASE_URL: {
    description: "Public Fastify API origin used by the browser app.",
    kind: "url",
  },
  VITE_APP_ENV: {
    choices: ["development", "preview", "staging", "production"],
    description: "Public deployment environment name.",
    kind: "enum",
  },
} as const satisfies EnvSchema;

export const apiEnvSchema = {
  APP_ENV: {
    choices: ["local", "test", "staging", "production"],
    description: "FleetMinder deployment environment.",
    kind: "enum",
  },
  BETTER_AUTH_COOKIE_DOMAIN: {
    description:
      "Optional parent domain for production Better Auth cookies, for example .fleetminder.app.",
    kind: "string",
    required: false,
  },
  BETTER_AUTH_SECRET: {
    description: "Better Auth signing secret used by the API only.",
    kind: "string",
    minLength: 32,
  },
  BETTER_AUTH_SECRETS: {
    description:
      "Optional comma-separated Better Auth secret rotation entries in version:secret format.",
    kind: "string",
    required: false,
  },
  BETTER_AUTH_SESSION_DAYS: {
    description: "Better Auth session lifetime in whole days.",
    kind: "integer",
    minValue: 1,
  },
  BETTER_AUTH_URL: {
    description: "Canonical API/auth origin.",
    kind: "url",
  },
  DATABASE_URL: {
    description: "Pooled PostgreSQL application connection string.",
    kind: "postgres-url",
  },
  DIRECT_DATABASE_URL: {
    description: "Direct PostgreSQL migration connection string.",
    kind: "postgres-url",
    required: false,
  },
  NODE_ENV: {
    choices: ["development", "test", "production"],
    description: "Node runtime mode.",
    kind: "enum",
  },
  OAUTH_GOOGLE_CLIENT_ID: {
    description: "Google OAuth public client ID, if enabled.",
    kind: "string",
    required: false,
  },
  OAUTH_GOOGLE_CLIENT_ENVIRONMENT: {
    choices: ["local", "staging", "production"],
    description:
      "Deployment environment the configured Google OAuth client was created for.",
    kind: "enum",
    required: false,
  },
  OAUTH_GOOGLE_CLIENT_SECRET: {
    description: "Google OAuth client secret, if enabled.",
    kind: "string",
    required: false,
  },
  PORT: {
    description: "Fastify listen port.",
    kind: "integer",
  },
  SSO_CLIENT_SECRET: {
    description: "SSO provider client secret, if enabled.",
    kind: "string",
    required: false,
  },
  S3_ACCESS_KEY_ID: {
    description: "S3-compatible object-storage access key.",
    kind: "string",
    required: false,
  },
  S3_BUCKET: {
    description: "Private S3-compatible object-storage bucket.",
    kind: "string",
    required: false,
  },
  S3_ENDPOINT: {
    description: "Optional S3-compatible endpoint, for example MinIO.",
    kind: "url",
    required: false,
  },
  S3_REGION: {
    description: "S3-compatible object-storage region.",
    kind: "string",
    required: false,
  },
  S3_SECRET_ACCESS_KEY: {
    description: "S3-compatible object-storage secret key.",
    kind: "string",
    required: false,
  },
  AUTH_TRUSTED_ORIGINS: {
    description:
      "Extra origins Better Auth accepts callbacks and redirects for, comma separated. A native client adds its deep-link scheme here, for example fleetminder://. Wildcards are refused outside local development.",
    kind: "string",
    required: false,
  },
  EMAIL_FROM: {
    description:
      "Address transactional email is sent from. Defaults to a no-reply address when unset.",
    kind: "string",
    required: false,
  },
  SES_CONFIGURATION_SET: {
    description:
      "SES configuration set used to report bounces and complaints. Without it SES still delivers, but delivery events go nowhere.",
    kind: "string",
    required: false,
  },
  SES_REGION: {
    description:
      "AWS region for the SES v2 API. Set this to send through SES; it takes precedence over SMTP_URL.",
    kind: "string",
    required: false,
  },
  SMTP_URL: {
    description:
      "Transactional email transport, for example smtp://localhost:1025 for local Mailpit. Without it, and without SES_REGION, messages are logged rather than sent.",
    kind: "string",
    required: false,
  },
  WEB_ORIGIN: {
    description: "Production or preview web origin allowed by CORS.",
    kind: "url",
  },
} as const satisfies EnvSchema;

export const workerEnvSchema = {
  DATABASE_URL: {
    description: "Pooled PostgreSQL worker connection string.",
    kind: "postgres-url",
  },
  EMAIL_API_KEY: {
    description: "Transactional email provider credential, if enabled.",
    kind: "string",
    required: false,
  },
  EMAIL_FROM: {
    description:
      "Address reminder email is sent from. Defaults to a no-reply address when unset.",
    kind: "string",
    required: false,
  },
  NODE_ENV: {
    choices: ["development", "test", "production"],
    description: "Node runtime mode.",
    kind: "enum",
  },
  S3_ACCESS_KEY_ID: {
    description: "S3-compatible object-storage access key.",
    kind: "string",
    required: false,
  },
  S3_BUCKET: {
    description: "Private S3-compatible object-storage bucket.",
    kind: "string",
    required: false,
  },
  SES_CONFIGURATION_SET: {
    description:
      "SES configuration set used to report bounces and complaints, as for the API.",
    kind: "string",
    required: false,
  },
  SES_REGION: {
    description:
      "AWS region for the SES v2 API. Takes precedence over SMTP_URL, as for the API.",
    kind: "string",
    required: false,
  },
  SMTP_URL: {
    description:
      "Email transport for queued notifications. Without it, and without SES_REGION, messages are logged rather than sent.",
    kind: "string",
    required: false,
  },
  WEB_ORIGIN: {
    description:
      "Web origin that links in notification email point to. Without it, queued email is not sent.",
    kind: "url",
    required: false,
  },
  S3_ENDPOINT: {
    description: "Optional S3-compatible endpoint.",
    kind: "url",
    required: false,
  },
  S3_REGION: {
    description: "S3-compatible object-storage region.",
    kind: "string",
    required: false,
  },
  S3_SECRET_ACCESS_KEY: {
    description: "S3-compatible object-storage secret key.",
    kind: "string",
    required: false,
  },
  WORKER_HEALTH_PORT: {
    description:
      "Port for the worker's liveness and readiness probes. Defaults to 3002.",
    kind: "integer",
    minValue: 1,
    required: false,
  },
} as const satisfies EnvSchema;

export const serverOnlyEnvNames = [
  "BETTER_AUTH_SECRET",
  "BETTER_AUTH_SECRETS",
  "DATABASE_URL",
  "DIRECT_DATABASE_URL",
  "OAUTH_CLIENT_SECRET",
  "OAUTH_GOOGLE_CLIENT_SECRET",
  "POSTGRES_URL",
  "SSO_CLIENT_SECRET",
  "S3_SECRET_ACCESS_KEY",
] as const;

export function validateWebEnv(
  source: EnvSource,
): ValidatedEnv<typeof webEnvSchema> {
  assertNoServerOnlyWebEnv(source);
  return validateEnv("web", webEnvSchema, source);
}

export function validateApiEnv(
  source: EnvSource,
): ValidatedEnv<typeof apiEnvSchema> {
  return validateEnv("api", apiEnvSchema, source);
}

export function validateWorkerEnv(
  source: EnvSource,
): ValidatedEnv<typeof workerEnvSchema> {
  return validateEnv("worker", workerEnvSchema, source);
}

export function assertNoServerOnlyWebEnv(source: EnvSource): void {
  const forbiddenKeys = findForbiddenWebEnvKeys(source);

  if (forbiddenKeys.length === 0) {
    return;
  }

  throw new EnvValidationError(
    "web",
    forbiddenKeys.map((name) => ({
      message:
        "server-only variable must not be exposed to the frontend bundle",
      name,
    })),
  );
}

export function findForbiddenWebEnvKeys(source: EnvSource): readonly string[] {
  return Object.keys(source).filter((name) => {
    if (!name.startsWith("VITE_")) {
      return false;
    }

    const unprefixedName = name.slice("VITE_".length);
    return serverOnlyEnvNames.some(
      (serverOnlyName) => unprefixedName === serverOnlyName,
    );
  });
}

export function validateEnv<TSchema extends EnvSchema>(
  scope: string,
  schema: TSchema,
  source: EnvSource,
): ValidatedEnv<TSchema> {
  const output = {} as Record<keyof TSchema, string | undefined>;
  const issues: EnvIssue[] = [];

  for (const [name, field] of typedEntries(schema)) {
    const rawValue = source[name];
    const isRequired = field.required !== false;

    if (!rawValue) {
      if (isRequired) {
        issues.push({ message: "is required", name });
      }

      output[name] = undefined;
      continue;
    }

    const invalidMessage = validateFieldValue(field, rawValue);

    if (invalidMessage) {
      issues.push({ message: invalidMessage, name });
      continue;
    }

    output[name] = rawValue;
  }

  if (issues.length > 0) {
    throw new EnvValidationError(scope, issues);
  }

  return output as ValidatedEnv<TSchema>;
}

function validateFieldValue(
  field: EnvField,
  value: string,
): string | undefined {
  if (field.kind === "enum") {
    return field.choices.includes(value)
      ? undefined
      : `must be one of ${field.choices.join(", ")}`;
  }

  if (field.minLength && value.length < field.minLength) {
    return `must be at least ${field.minLength} characters`;
  }

  if (field.kind === "integer" && !/^\d+$/.test(value)) {
    return "must be an integer";
  }

  if (
    field.kind === "integer" &&
    typeof field.minValue === "number" &&
    Number.parseInt(value, 10) < field.minValue
  ) {
    return `must be at least ${field.minValue}`;
  }

  if (field.kind === "postgres-url" && !/^postgres(ql)?:\/\//.test(value)) {
    return "must be a PostgreSQL connection URL";
  }

  if (field.kind === "url" && !/^https?:\/\/[^/]+/.test(value)) {
    return "must be an HTTP(S) URL";
  }

  return undefined;
}

function formatEnvIssues(scope: string, issues: readonly EnvIssue[]): string {
  const details = issues
    .map((issue) => `- ${issue.name}: ${issue.message}`)
    .join("\n");
  return `Invalid ${scope} environment:\n${details}`;
}

function typedEntries<T extends Record<string, unknown>>(
  value: T,
): [keyof T & string, T[keyof T]][] {
  return Object.entries(value) as [keyof T & string, T[keyof T]][];
}
